import { useMemo } from "react";
import { AlertTriangle, ChevronRight, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { anchorSubject, presentError, routeErrors } from "@/services/multi-agent/errorRouting";
import type { Action, Error as ProtocolError } from "@/services/multi-agent/protocol";
import { selectActions, selectErrors, useRunStateStore } from "@/stores/runStateStore";

/**
 * Run-wide failures, and only those (contract §2.18, §4.9; handoff Tier 2 "typed error surface").
 *
 * WHAT THIS DELIBERATELY DOES NOT SHOW. Anything `routeErrors` sends to an anchor — a part, a tool
 * call, a deploy phase, a component. Those render at the thing that failed, because a pile of five
 * phase errors at the top of the screen reads as five unrelated catastrophes instead of one bad
 * deploy step, and because an error shown in both places makes the reader count one failure twice.
 * The one exception is an ORPHAN: an anchored error whose anchor id the server omitted. It surfaces
 * here with its subject named as unidentified, because being wrongly placed is recoverable and
 * being invisible is not.
 *
 * `state.errors` IS CUMULATIVE (§2.18) — it holds every error the run has produced, including ones
 * that were subsequently retried and succeeded. So this is headed "recorded during this run" and
 * not "the run is broken": a completed run with two recovered timeouts is a success, and copy
 * implying otherwise would send the reader after a problem that is over.
 */
export interface RunErrorRailViewProps {
  errors: readonly ProtocolError[];
  /** Dispatch a backend-offered action through the route's shared controller. */
  onAction?: (action: Action) => void;
  /**
   * Actions the ACTION BAR is already offering, so this rail does not offer them a second time.
   *
   * THE DUPLICATE THIS REMOVES, seen in the browser on the `errors` scenario: an `SF_AUTH_EXPIRED`
   * error carries `action: {label: "Reconnect org", action: "reconnect_org"}`, and the same run's
   * `state.actions.available` carries `reconnect_org` too — both legitimate per the contract
   * (§4.10 gives the error an action; §3.2 gives the bar one). The client is the only place that
   * can notice they are the same button, and before this it rendered both at once: one inside the
   * red rail, one in the bar below, identical labels, identical effect.
   *
   * THE BAR WINS, and the reason is reachability rather than hierarchy. The rail is capped and
   * scrollable (and collapses past `RUN_ERRORS_SHOWN`), so an action offered only here can scroll
   * out of view; the bar is always on screen. Suppressing the bar's copy instead could leave a
   * backend-offered action unreachable, which is the worse failure. The rail keeps the error's
   * `hint`, so the reader still learns WHAT to do where the explanation is — they just press the
   * one button that exists.
   *
   * Defaults to empty, so a caller with no action bar (the QA harness) keeps every button.
   */
  offeredElsewhere?: readonly Action[];
  busy?: boolean;
  className?: string;
}

/**
 * How many run-wide errors are shown before the rest collapse.
 *
 * Three, because a deploy reports one error per component and they repeat: three is enough to see
 * the SHAPE of the failure (same message, different field) without the rail becoming the page.
 */
const RUN_ERRORS_SHOWN = 3;

export function RunErrorRailView({
  errors,
  onAction,
  offeredElsewhere = [],
  busy,
  className,
}: RunErrorRailViewProps) {
  const routed = routeErrors(errors);
  // Nothing run-wide is the normal case, including on a run that had anchored failures.
  if (!routed.run.length) return null;

  return (
    <section
      aria-labelledby="weave-errors-title"
      className={cn("rounded-md border border-ma-err-line bg-ma-err-soft", className)}
    >
      <header className="px-3.5 py-2.5">
        <h3 id="weave-errors-title" className="text-[13px] font-semibold text-foreground">
          {routed.run.length === 1 ? "Something went wrong" : `${routed.run.length} problems during this run`}
        </h3>
        {/*
          Says "during this run", because `state.errors` is cumulative: an error that was retried and
          succeeded stays in the list. Copy implying the run is currently broken would send the
          reader after a problem that is over.
        */}
        <p className="mt-0.5 text-[12px] text-muted-foreground">
          Recorded during this run. Some may already have been retried successfully.
        </p>
      </header>

      {/*
        THE FIRST FEW, THEN A COUNT.
        
        A failed deploy of fifteen components reports one error per component, and they differ only
        in the field name: twelve rows of "Validation did not pass — Validation: failed (1 error) —
        StripeIntegrationSettings__c.<field>". Rendered in full they were the entire screen, and the
        thirteenth row said nothing the third had not. Reported from a real run.
        
        A HEAD SLICE, not a filter: the first ones are the ones the reader acts on, and the rest are
        one click away with their own count. `CollapsibleMarkdownList` makes the same trade for long
        markdown lists, for the same reason.
      */}
      <ul className="border-t border-ma-err-line">
        {routed.run.slice(0, RUN_ERRORS_SHOWN).map((error, index) => {
          const shown = presentError(error);
          const subject = anchorSubject(error);
          return (
            <li
              key={`${error.code}-${index}`}
              className="border-b border-ma-err-line px-3.5 py-2.5 last:border-b-0"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle aria-hidden className="mt-[3px] h-3.5 w-3.5 shrink-0 text-ma-err-text" />
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium text-foreground">{shown.headline}</p>
                  {/* The server's own words, never rewritten — the most specific thing on screen. */}
                  {shown.detail && (
                    <p className="mt-0.5 break-words text-[12px] leading-snug text-muted-foreground">
                      {shown.detail}
                    </p>
                  )}
                  {shown.hint && (
                    <p className="mt-1 flex items-start gap-1.5 text-[12px] leading-snug text-foreground">
                      <Info aria-hidden className="mt-[3px] h-3 w-3 shrink-0 text-muted-foreground" />
                      <span className="min-w-0">{shown.hint}</span>
                    </p>
                  )}
                  {/*
                    An orphan says what it was about. Naming an id the server did not send would be
                    fabrication; "not identified" is the true version.
                  */}
                  {subject && (
                    <p className="mt-1 text-[12px] text-muted-foreground">Reported about {subject}.</p>
                  )}
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    {/*
                      The backend's own offered action, dispatched through the SAME controller as the
                      action bar — so `deploy` from here is confirmed and gated exactly as it is
                      there. An error banner must not become a second way to write to an org.
                    */}
                    {shown.action && onAction && !offeredElsewhere.includes(shown.action.action) && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onAction(shown.action!.action)}
                        className="rounded-md border border-border bg-background px-2 py-1 text-[12px] font-medium text-foreground hover:bg-muted/50 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {shown.action.label}
                      </button>
                    )}
                    {/*
                      Read off `retryable` alone, never inferred from the code. `TOOL_TIMEOUT` sounds
                      transient, but the server knows whether THIS instance can be retried.
                    */}
                    {shown.retryable && !shown.action && (
                      <span className="text-[12px] text-muted-foreground">This one can be retried.</span>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {routed.run.length > RUN_ERRORS_SHOWN && (
        <details className="group border-t border-ma-err-line">
          <summary className="flex cursor-pointer list-none items-center gap-1.5 px-3.5 py-2 text-[12px] text-muted-foreground transition-colors motion-reduce:transition-none hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <ChevronRight
              aria-hidden
              className="h-3.5 w-3.5 shrink-0 transition-transform group-open:rotate-90 motion-reduce:transition-none"
            />
            {/* The count is the point: it says how much is hidden, not just that something is. */}
            {routed.run.length - RUN_ERRORS_SHOWN} more
          </summary>
          <ul>
            {routed.run.slice(RUN_ERRORS_SHOWN).map((error, index) => {
              const shown = presentError(error);
              return (
                <li
                  key={`${error.code}-rest-${index}`}
                  className="border-t border-ma-err-line px-3.5 py-2 pl-9"
                >
                  <p className="text-[13px] font-medium text-foreground">{shown.headline}</p>
                  {shown.detail && (
                    <p className="mt-0.5 break-words text-[12px] leading-snug text-muted-foreground">
                      {shown.detail}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </details>
      )}
    </section>
  );
}

export interface RunErrorRailProps {
  onAction?: (action: Action) => void;
  busy?: boolean;
  className?: string;
}

/** Wired. Split from the view for the reason `ActionBar`/`ActionBarView` are split. */
export function RunErrorRail({ onAction, busy, className }: RunErrorRailProps) {
  const errors = useRunStateStore(selectErrors);
  /*
   * The bar's own offer list, read from the SAME store selector the bar renders from — so the two
   * surfaces cannot disagree about what is already on screen. `primary` is included because it is
   * a rendered button too, not a separate kind of thing.
   */
  const actions = useRunStateStore(selectActions);
  const offeredElsewhere = useMemo(
    () => [actions.primary, ...actions.available],
    [actions.primary, actions.available],
  );
  return (
    <RunErrorRailView
      errors={errors}
      onAction={onAction}
      offeredElsewhere={offeredElsewhere}
      busy={busy}
      className={className}
    />
  );
}
